import { useTranslation } from '../../../i18n';
import React, { useCallback, useEffect, useState } from 'react';
import { Card, Space, Segmented, Select, Input, Button, Empty, Typography, message } from 'antd';
import { ReloadOutlined, DownloadOutlined } from '@ant-design/icons';
import hostSharedService from '../../../../shared/services/hostSharedService';

/** 站点日志:访问日志 / PHP 错误日志的最近 N 行(按状态码、路径过滤),下载今天的日志 */
const SiteLogs = ({ vhostId }) => {
    const { t } = useTranslation();
    const [kind, setKind] = useState('access');
    const [lines, setLines] = useState(200);
    const [status, setStatus] = useState('');
    const [path, setPath] = useState('');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async (k = kind) => {
        if (!vhostId) return;
        setLoading(true);
        const params = { kind: k, lines };
        if (k === 'access' && status.trim()) params.status = status.trim();
        if (path.trim()) params.path = path.trim();
        const res = await hostSharedService.getSiteLogs(vhostId, params);
        setLoading(false);
        if (res?.success) {
            setData(res.data);
        } else {
            setData(null);
            message.error(res?.message || res?.error || t('Failed to load logs'));
        }
    }, [vhostId, kind, lines, status, path, t]);

    useEffect(() => {
        load(kind);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vhostId, kind, lines]);

    const download = async () => {
        try {
            const url = await hostSharedService.siteLogDownloadUrl(vhostId, kind);
            window.open(url, '_blank', 'noopener');
        } catch (e) {
            message.error(t('Download failed'));
        }
    };

    const rows = data?.lines || [];

    return (
        <Card
            title={t('Site logs')}
            extra={
                <Space wrap>
                    <Button icon={<ReloadOutlined />} onClick={() => load()} loading={loading}>{t('Refresh')}</Button>
                    <Button icon={<DownloadOutlined />} onClick={download}>{t("Download today's log")}</Button>
                </Space>
            }
        >
            <Space wrap style={{ marginBottom: 12 }}>
                <Segmented
                    value={kind}
                    onChange={setKind}
                    options={[{ label: t('Access log'), value: 'access' }, { label: t('PHP errors'), value: 'error' }]}
                />
                <Select
                    value={lines}
                    onChange={setLines}
                    style={{ width: 120 }}
                    options={[100, 200, 500, 1000, 2000].map((n) => ({ value: n, label: t('Last {{n}} lines', { n }) }))}
                />
                {kind === 'access' && (
                    <Input
                        allowClear
                        style={{ width: 130 }}
                        placeholder={t('Status, e.g. 404')}
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        onPressEnter={() => load()}
                    />
                )}
                <Input.Search
                    allowClear
                    style={{ width: 240 }}
                    placeholder={kind === 'access' ? t('Path contains') : t('Contains')}
                    value={path}
                    onChange={(e) => setPath(e.target.value)}
                    onSearch={() => load()}
                />
            </Space>
            {data && data.exists === false ? (
                <Empty description={t('No log yet')} />
            ) : rows.length === 0 && !loading ? (
                <Empty description={t('No matching lines')} />
            ) : (
                <pre
                    style={{
                        maxHeight: 520,
                        overflow: 'auto',
                        fontSize: 12,
                        lineHeight: 1.5,
                        background: 'var(--ant-color-fill-quaternary, rgba(0,0,0,0.04))',
                        padding: 12,
                        borderRadius: 6,
                        margin: 0,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all'
                    }}
                >
                    {rows.join('\n')}
                </pre>
            )}
            {data && data.scanned_all === false && rows.length < lines && (
                <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                    {t('Only the newest part of a large log was searched; download it for the rest.')}
                </Typography.Text>
            )}
        </Card>
    );
};

export default SiteLogs;
